import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { Card, Button, Input, Modal, Select } from '@components/common/UIComponents';
import {
  addMeal,
  getMealsForDate,
  getRecentMeals,
  getMealsInRange,
  deleteMeal,
  searchFoodsByName
} from '@services/firestoreService';
import { Utensils, Plus, Trash2, Clock, Flame, ChevronLeft, ChevronRight, Award } from 'lucide-react';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths, subDays, subWeeks, subMonths, eachDayOfInterval } from 'date-fns';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MEAL_TYPES = [
  'Breakfast',
  'Lunch',
  'Dinner',
  'Snacks',
  'Pre-workout',
  'Post-workout'
];

const NutritionPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('week'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [meals, setMeals] = useState([]); // Today's meals
  const [periodData, setPeriodData] = useState([]); // Period meals for charts
  const [recentMeals, setRecentMeals] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [foodSuggestions, setFoodSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const searchTimeoutRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'Breakfast',
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
    servingSize: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [user, period, currentDate]);

  const getPeriodRange = () => {
    if (period === 'day') {
      return {
        start: startOfDay(currentDate),
        end: endOfDay(currentDate)
      };
    } else if (period === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }), // Monday
        end: endOfWeek(currentDate, { weekStartsOn: 1 })
      };
    } else {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate)
      };
    }
  };

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Load today's meals
      const todayMeals = await getMealsForDate(user.uid, new Date());
      setMeals(todayMeals);

      // Load period data
      const { start, end } = getPeriodRange();
      const periodMeals = await getMealsInRange(user.uid, start, end);
      setPeriodData(periodMeals);

      // Load recent meals
      const recent = await getRecentMeals(user.uid, 10);
      setRecentMeals(recent);
    } catch (error) {
      console.error('Error loading nutrition data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (direction) => {
    if (period === 'day') {
      setCurrentDate(direction === 'prev' ? subDays(currentDate, 1) : addDays(currentDate, 1));
    } else if (period === 'week') {
      setCurrentDate(direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
    }
  };

  const getPeriodLabel = () => {
    if (period === 'day') {
      return format(currentDate, 'MMMM dd, yyyy');
    } else if (period === 'week') {
      const { start, end } = getPeriodRange();
      return `${format(start, 'MMM dd')} - ${format(end, 'MMM dd, yyyy')}`;
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Trigger autocomplete search for food name
    if (name === 'name' && value.length >= 2) {
      // Debounce the search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const suggestions = await searchFoodsByName(value);
          setFoodSuggestions(suggestions);
          setShowSuggestions(suggestions.length > 0);
        } catch (error) {
          console.error('Error searching foods:', error);
        }
      }, 300);
    } else if (name === 'name' && value.length < 2) {
      setFoodSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectFood = (food) => {
    setSelectedFood(food);
    setFormData({
      ...formData,
      name: food.name,
      calories: food.calories.toString(),
      protein: food.protein.toString(),
      carbs: food.carbs.toString(),
      fat: food.fat.toString(),
      servingSize: food.servingSize || ''
    });
    setShowSuggestions(false);
    setFoodSuggestions([]);
  };

  const handleAddMeal = async () => {
    if (!formData.name || !formData.calories) return;

    try {
      await addMeal(user.uid, {
        name: formData.name,
        type: formData.type,
        calories: parseInt(formData.calories) || 0,
        protein: parseInt(formData.protein) || 0,
        carbs: parseInt(formData.carbs) || 0,
        fat: parseInt(formData.fat) || 0,
        notes: formData.notes
      });

      setShowAddModal(false);
      setFormData({
        name: '',
        type: 'Breakfast',
        calories: '',
        protein: '',
        carbs: '',
        fat: '',
        notes: ''
      });
      loadData();
    } catch (error) {
      console.error('Error adding meal:', error);
    }
  };

  const handleDeleteMeal = async (mealId) => {
    try {
      await deleteMeal(user.uid, mealId);
      loadData();
    } catch (error) {
      console.error('Error deleting meal:', error);
    }
  };

  const totalCalories = meals.reduce((sum, meal) => sum + (meal.calories || 0), 0);
  const totalProtein = meals.reduce((sum, meal) => sum + (meal.protein || 0), 0);
  const totalCarbs = meals.reduce((sum, meal) => sum + (meal.carbs || 0), 0);
  const totalFat = meals.reduce((sum, meal) => sum + (meal.fat || 0), 0);

  // Group meals by type
  const mealsByType = meals.reduce((acc, meal) => {
    if (!acc[meal.type]) acc[meal.type] = [];
    acc[meal.type].push(meal);
    return acc;
  }, {});

  // Calculate period statistics
  const { start, end } = getPeriodRange();
  const allDays = eachDayOfInterval({ start, end });
  
  // Group period data by day
  const mealsByDay = allDays.map(day => {
    const dayId = format(day, 'yyyy-MM-dd');
    const dayMeals = periodData.filter(meal => {
      const mealDate = meal.date ? new Date(meal.date) : new Date();
      return format(mealDate, 'yyyy-MM-dd') === dayId;
    });
    
    return {
      date: day,
      dateId: dayId,
      calories: dayMeals.reduce((sum, m) => sum + (m.calories || 0), 0),
      protein: dayMeals.reduce((sum, m) => sum + (m.protein || 0), 0),
      carbs: dayMeals.reduce((sum, m) => sum + (m.carbs || 0), 0),
      fat: dayMeals.reduce((sum, m) => sum + (m.fat || 0), 0),
      meals: dayMeals
    };
  });

  // Calculate statistics
  const calculateStats = (field) => {
    const values = mealsByDay.map(day => day[field]);
    const total = values.reduce((sum, val) => sum + val, 0);
    const average = Math.round(total / values.length) || 0;
    const max = Math.max(...values, 0);
    return { total, average, max };
  };

  const calorieStats = calculateStats('calories');
  const proteinStats = calculateStats('protein');
  const carbsStats = calculateStats('carbs');
  const fatStats = calculateStats('fat');

  // Prepare chart data
  const getChartLabels = () => {
    if (period === 'day') {
      return ['Today'];
    } else if (period === 'week') {
      return allDays.map(day => format(day, 'EEE')); // Mon, Tue, Wed...
    } else {
      // For month, show every few days
      return allDays.filter((_, index) => index % Math.ceil(allDays.length / 10) === 0)
                    .map(day => format(day, 'MMM d'));
    }
  };

  const getChartData = (field) => {
    if (period === 'day') {
      const todayData = mealsByDay.find(day => 
        format(day.date, 'yyyy-MM-dd') === format(currentDate, 'yyyy-MM-dd')
      );
      return [todayData?.[field] || 0];
    }
    
    return mealsByDay.map(day => day[field]);
  };

  const caloriesChartData = {
    labels: getChartLabels(),
    datasets: [
      {
        label: 'Calories',
        data: getChartData('calories'),
        backgroundColor: 'rgba(249, 115, 22, 0.8)',
        borderColor: 'rgb(249, 115, 22)',
        borderWidth: 1
      }
    ]
  };

  const macrosChartData = {
    labels: getChartLabels(),
    datasets: [
      {
        label: 'Protein (g)',
        data: getChartData('protein'),
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderColor: 'rgb(239, 68, 68)',
        borderWidth: 1
      },
      {
        label: 'Carbs (g)',
        data: getChartData('carbs'),
        backgroundColor: 'rgba(234, 179, 8, 0.8)',
        borderColor: 'rgb(234, 179, 8)',
        borderWidth: 1
      },
      {
        label: 'Fat (g)',
        data: getChartData('fat'),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => value.toLocaleString()
        }
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Nutrition Tracking</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track your meals and daily nutrition
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus size={20} className="mr-2" />
          Add Meal
        </Button>
      </div>

      {/* Period Selector */}
      <Card>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Period Tabs */}
          <div className="flex gap-2">
            <Button
              variant={period === 'day' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod('day')}
            >
              Daily
            </Button>
            <Button
              variant={period === 'week' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod('week')}
            >
              Weekly
            </Button>
            <Button
              variant={period === 'month' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod('month')}
            >
              Monthly
            </Button>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => handleNavigate('prev')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[200px] text-center">
              {getPeriodLabel()}
            </span>
            <button
              onClick={() => handleNavigate('next')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              disabled={format(currentDate, 'yyyy-MM-dd') >= format(new Date(), 'yyyy-MM-dd')}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </Card>

      {/* Today's Summary */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Today's Intake</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="text-center">
            <Flame className="mx-auto mb-2 text-orange-600 dark:text-orange-400" size={32} />
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{totalCalories}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Calories</p>
          </Card>
          <Card className="text-center">
            <div className="w-8 h-8 mx-auto mb-2 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <span className="text-red-600 dark:text-red-400 font-bold text-sm">P</span>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{totalProtein}g</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Protein</p>
          </Card>
          <Card className="text-center">
            <div className="w-8 h-8 mx-auto mb-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center">
              <span className="text-yellow-600 dark:text-yellow-400 font-bold text-sm">C</span>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{totalCarbs}g</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Carbs</p>
          </Card>
          <Card className="text-center">
            <div className="w-8 h-8 mx-auto mb-2 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">F</span>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{totalFat}g</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Fat</p>
          </Card>
        </div>
      </div>

      {/* Period Statistics - Calories */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Calorie Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {calorieStats.total.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Calories</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {calorieStats.average.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Avg Calories/Day</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {calorieStats.max.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Highest Day</div>
            </div>
          </Card>
        </div>
      </div>

      {/* Calories Chart */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Calories Overview
        </h3>
        <div className="h-64">
          <Bar data={caloriesChartData} options={chartOptions} />
        </div>
      </Card>

      {/* Period Statistics - Macros */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Macros Statistics</h2>
        <div className="grid grid-cols-3 md:grid-cols-9 gap-4">
          {/* Protein */}
          <Card>
            <div className="text-center">
              <div className="text-xl font-bold text-red-600 dark:text-red-400">
                {proteinStats.total}g
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Total Protein</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-xl font-bold text-red-600 dark:text-red-400">
                {proteinStats.average}g
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Avg Protein</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-xl font-bold text-red-600 dark:text-red-400">
                {proteinStats.max}g
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Best Day</div>
            </div>
          </Card>
          {/* Carbs */}
          <Card>
            <div className="text-center">
              <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400">
                {carbsStats.total}g
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Total Carbs</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400">
                {carbsStats.average}g
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Avg Carbs</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400">
                {carbsStats.max}g
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Best Day</div>
            </div>
          </Card>
          {/* Fat */}
          <Card>
            <div className="text-center">
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                {fatStats.total}g
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Total Fat</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                {fatStats.average}g
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Avg Fat</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                {fatStats.max}g
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Best Day</div>
            </div>
          </Card>
        </div>
      </div>

      {/* Macros Chart */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Macros Overview
        </h3>
        <div className="h-64">
          <Bar data={macrosChartData} options={chartOptions} />
        </div>
      </Card>

      {/* Meals Timeline */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Today's Meals ({meals.length})
        </h3>

        {meals.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Utensils size={48} className="mx-auto mb-3 opacity-30" />
            <p>No meals logged yet today</p>
            <p className="text-sm mt-1">Start tracking your nutrition by adding a meal</p>
          </div>
        ) : (
          <div className="space-y-6">
            {MEAL_TYPES.map((type) => {
              const typeMeals = mealsByType[type] || [];
              if (typeMeals.length === 0) return null;

              return (
                <div key={type} className="border-l-4 border-primary-500 pl-4">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    {type}
                  </h4>
                  <div className="space-y-3">
                    {typeMeals.map((meal) => (
                      <div
                        key={meal.id}
                        className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900 dark:text-gray-100">
                              {meal.name}
                            </h5>
                            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                              <Clock size={12} />
                              {format(new Date(meal.date), 'h:mm a')}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteMeal(meal.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div>
                            <p className="text-gray-600 dark:text-gray-400 text-xs">Calories</p>
                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                              {meal.calories}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400 text-xs">Protein</p>
                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                              {meal.protein}g
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400 text-xs">Carbs</p>
                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                              {meal.carbs}g
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400 text-xs">Fat</p>
                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                              {meal.fat}g
                            </p>
                          </div>
                        </div>
                        {meal.notes && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic">
                            {meal.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Add Meal Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setShowSuggestions(false);
          setFoodSuggestions([]);
          setSelectedFood(null);
        }}
        title="Add Meal"
      >
        <div className="space-y-4">
          {/* Food Name with Autocomplete */}
          <div className="relative">
            <Input
              name="name"
              label="Meal Name"
              placeholder="Start typing food name... (e.g., Paneer Tikka)"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            
            {/* Autocomplete Suggestions */}
            {showSuggestions && foodSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {foodSuggestions.map((food) => (
                  <button
                    key={food.id}
                    type="button"
                    onClick={() => handleSelectFood(food)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {food.name}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {food.servingSize} • {food.category}
                        </p>
                      </div>
                      <div className="text-right ml-3">
                        <p className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                          {food.calories} cal
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          P:{food.protein}g C:{food.carbs}g F:{food.fat}g
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {selectedFood && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                ✓ Selected: {selectedFood.servingSize}
              </p>
            )}
          </div>

          <Select
            name="type"
            label="Meal Type"
            value={formData.type}
            onChange={handleInputChange}
            options={MEAL_TYPES.map(type => ({ value: type, label: type }))}
            fullWidth
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              name="calories"
              label="Calories"
              placeholder="450"
              value={formData.calories}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <Input
              type="number"
              name="protein"
              label="Protein (g)"
              placeholder="35"
              value={formData.protein}
              onChange={handleInputChange}
              fullWidth
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              name="carbs"
              label="Carbs (g)"
              placeholder="20"
              value={formData.carbs}
              onChange={handleInputChange}
              fullWidth
            />
            <Input
              type="number"
              name="fat"
              label="Fat (g)"
              placeholder="15"
              value={formData.fat}
              onChange={handleInputChange}
              fullWidth
            />
          </div>

          <Input
            name="notes"
            label="Notes (optional)"
            placeholder="Add any additional details..."
            value={formData.notes}
            onChange={handleInputChange}
            fullWidth
          />

          <div className="flex gap-3 pt-2">
            <Button onClick={handleAddMeal} fullWidth>
              Add Meal
            </Button>
            <Button variant="secondary" onClick={() => {
              setShowAddModal(false);
              setShowSuggestions(false);
              setFoodSuggestions([]);
              setSelectedFood(null);
            }} fullWidth>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default NutritionPage;
